<template>
  <div class="columns">
    <div class="column is-7">
      <div class="document description">
        <table>
          <tr>
            <td><strong>{{ $t('documents.fields.author') }}</strong></td>
            <td>
              <a @click="userDetail(detail.author.id)">{{ detail.author.initials }}</a>
            </td>
            <td><strong>{{ $t('documents.fields.term') }}</strong></td>
            <td v-if="detail.term_date">{{ detail.term_date | localedDate }}</td>
            <td v-else>&ndash;</td>
          </tr>
          <tr>
            <td><strong>{{ $t('documents.filters.priority') }}</strong></td>
            <td>
              <b-tag :type="`is-${priorityColors[detail.priority]}`">
                {{ $t(`workflow.priorities.${detail.priority}`) }}
              </b-tag>
            </td>
            <td><strong>{{ $t('documents.filters.state') }}</strong></td>
            <td>
              <b-tag :type="`is-${stateColors[detail.state]}`">
                {{ $t(`workflow.states.${detail.state}`) }}
              </b-tag>
            </td>
          </tr>
          <tr>
            <td><strong>{{ $t('documents.filters.type') }}</strong></td>
            <td>{{ $t(`workflow.doctypes.${detail.type}`) }}</td>
            <td><strong>{{ $t('documents.executive_maintainer') }}</strong></td>
            <td v-if="detail.executive_maintainer !== null">
              <a @click="userDetail(detail.executive_maintainer)">{{ detail.executive_maintainer__initials }}</a>
            </td>
            <td v-else>&ndash;</td>
          </tr>
          <tr>
            <td><strong>{{ $t('interface.date') }}</strong></td>
            <td>{{ detail.created_date | localedDate }}</td>
            <td><strong>{{ $t('documents.maintainer') }}</strong></td>
            <td v-if="detail.maintainer !== null">
              <a @click="userDetail(detail.maintainer)">{{ detail.maintainer__initials }}</a>
            </td>
            <td v-else>&ndash;</td>
          </tr>
          <tr>
            <td><strong>{{ $t('documents.fields.sourceType') }}</strong></td>
            <td>{{ $t(`documents.sourceTypes.${detail.source_type}`) }}</td>
            <template v-if="detail.document">
              <td><strong>{{ $t('documents.document') }}</strong></td>
              <td>
                <a @click="documentDetail(detail.document.id, detail.document.category)"
                   class="underline">{{ detail.document.registration_number }}</a>
                <a :href="detail.document.file" class="download-document">
                  <b-icon icon="file-download-outline" size="is-small"></b-icon>
                </a>
              </td>
            </template>
          </tr>
        </table>
        <div>
          <strong>{{ $t('interface.description') }}</strong>
          <p v-html="detail.description"></p>
        </div>
      </div>
      <Attachments class="mt-15 mb-10"
                   size="large"
                   v-if="!detail.is_auto_generated"
                   @file-upload="uploadFile"
                   @remove-file="removeFile"
                   :editable="isAuthor || isMaintainer"
                   :upload="upload"
                   :title="$t('documents.attachments')"
                   :data="detail.attachments">
      </Attachments>
      <Comments :comments="detail.comments"
                v-if="!detail.is_auto_generated"
                @create="createComment"
                @edit="editComment"
                @remove="removeComment">
      </Comments>
    </div>
    <div class="column is-5">
      <h3 class="title is-6">{{ $t('documents.progress') }}</h3>
      <Timeline class="mt-15"
                :disabled="timelineDisable"
                :data="timeline">
      </Timeline>
    </div>
  </div>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'
  import Timeline from '@/components/Timeline'
  import Comments from '@/components/Comments'
  import {timelineItem} from '@/constants'
  import Attachments from '@/components/Attachments'
  import {priorityColors, stateColors} from '@/colorTypes'
  import {userDetail, documentDetail} from '@/utils'

  export default {
    data() {
      return {
        stateColors,
        priorityColors,
        file: null,
        timelineDisable: false
      }
    },
    components: {
      Timeline,
      Comments,
      Attachments
    },
    methods: {
      userDetail,
      documentDetail,
      uploadFile({file, type}) {
        this.timelineDisable = true
        this.$store.dispatch('Document/uploadFile', {file, type}).then(() => this.timelineDisable = false)
      },
      removeFile(fileId) {
        this.$store.dispatch('Document/removeFile', fileId).then(() => this.$toast.open(this.$t('interface.fileSuccessfulDeleted')))
      },
      createComment(text) {
        this.$store.dispatch('Document/createComment', text)
      },
      editComment(comment) {
        this.$store.dispatch('Document/updateComment', comment)
      },
      removeComment(id) {
        this.$store.dispatch('Document/removeComment', id)
      }
    },
    computed: {
      ...mapState('Document', ['detail', 'upload']),
      ...mapState('Account', ['account']),
      isAuthor() {
        return this.detail.author.id === this.account.employee.id
      },
      isMaintainer() {
        return this.detail.maintainer === this.account.employee.id
      },
      timeline() {
        return this.detail.tasks_states.map(state => {
          let title = state.task ?
                  `${this.$t('routes.taskDetail')} #${state.task} – ${this.$t(`workflow.states.${state.state}`)}`
                  : this.$t(`workflow.states.${state.state}`)
          return {
            ...timelineItem,
            date: state.created_at,
            title,
            note: `${state.author ? state.author.initials : ''}${state.note ? ` – ${state.note}` : ''}`,
            attachments: state.attachments,
          }
        })
      }
    }
  }
</script>

<style lang="scss">
  .document.description {
    color: #666;
    table {
      width: 100%;

      td {
        padding-bottom: 10px;
      }

      tr > td:first-child,
      tr > td:nth-child(3) {
        width: 140px;
      }
    }

    & > div {
      margin-top: 10px;
      & > p {
        margin-top: 4px;
      }
    }
  }

  .title.is-6 {
    margin-bottom: 0px;
  }

  .underline {
    border-bottom: 1px solid #444;
    padding-bottom: 1px;
    &:hover {
      border-bottom-color: transparent;
    }
  }

  .download-document {
    position: relative;
    top: 3px;
    margin-left: 4px;
  }
</style>
